/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * Le fatture per periodo di TUTTI gli interventi (migrazione 014).
 *
 * Nella scheda dell'intervento si vede un cantiere alla volta; la morosita' si
 * vede solo qui, tutti insieme: cosa e' da emettere, cosa aspetta il pagamento,
 * cosa e' scaduto. Le scadute stanno sempre in cima.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { interventiAPI } from '../api/interventi';
import { fmtGiorno } from '../utils/periodo';

const FILTRI = [
  { id: 'aperte',      label: 'Aperte' },
  { id: 'scadute',     label: 'Scadute' },
  { id: 'da_emettere', label: 'Da emettere' },
  { id: 'tutte',       label: 'Tutte' },
];

const STATO = {
  da_emettere: { label: 'Da emettere', bg: '#F3F4F6', color: '#374151' },
  emessa:      { label: 'Da incassare', bg: '#DBEAFE', color: '#1E40AF' },
  pagata:      { label: 'Pagata', bg: '#D1FAE5', color: '#065F46' },
};

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;

export default function Fatturazione() {
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState('aperte');
  const { data, isLoading, error } = useQuery({
    queryKey: ['fatturazioni', filtro],
    queryFn: () => interventiAPI.elencoFatturazioni(filtro),
  });

  return (
    <AdminLayout title="Fatturazione">
      <div style={s.cards}>
        <div style={s.card}><div style={s.cardLabel}>Da emettere</div><div style={s.cardValore}>{eur(data?.totali.da_emettere)}</div></div>
        <div style={s.card}><div style={s.cardLabel}>Da incassare</div><div style={{ ...s.cardValore, color: '#1E40AF' }}>{eur(data?.totali.da_incassare)}</div></div>
        <div style={{ ...s.card, ...(data?.totali.scaduto > 0 ? s.cardRossa : {}) }}>
          <div style={s.cardLabel}>Scaduto, non pagato</div>
          <div style={{ ...s.cardValore, color: data?.totali.scaduto > 0 ? '#B91C1C' : '#111827' }}>{eur(data?.totali.scaduto)}</div>
        </div>
      </div>
      <p style={s.nota}>I totali non contano gli interventi di prova. Importi IVA esclusa.</p>

      <div style={s.filtri}>
        {FILTRI.map((f) => (
          <button key={f.id} onClick={() => setFiltro(f.id)} style={{ ...s.filtroBtn, ...(filtro === f.id ? s.filtroAttivo : {}) }}>
            {f.label}
          </button>
        ))}
      </div>

      <div style={s.box}>
        {isLoading ? (
          <div style={s.vuoto}>Caricamento...</div>
        ) : error ? (
          <div style={s.vuoto}>{error.response?.data?.error || 'Errore nel caricamento'}</div>
        ) : data.fatturazioni.length === 0 ? (
          <div style={s.vuoto}>Niente in questo elenco.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.tabella}>
              <thead>
                <tr>
                  <th style={s.th}>Intervento</th>
                  <th style={s.th}>Cliente</th>
                  <th style={s.th}>Periodo</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Importo</th>
                  <th style={s.th}>Fattura</th>
                  <th style={s.th}>Stato</th>
                </tr>
              </thead>
              <tbody>
                {data.fatturazioni.map((f) => {
                  const st = STATO[f.stato];
                  return (
                    <tr key={f.id} style={s.tr} onClick={() => navigate(`/interventi/${f.intervento_id}`)}>
                      <td style={s.td}>
                        <code style={s.codice}>{f.codice}</code>
                        {f.di_prova && <span style={s.prova}>prova</span>}
                        <span style={s.sotto}>{f.titolo}</span>
                      </td>
                      <td style={s.td}>{f.cliente || '—'}</td>
                      <td style={s.td}>
                        {f.tipo === 'saldo' ? 'Saldo' : 'Acconto'}
                        <span style={s.sotto}>{fmtGiorno(f.periodo_dal)} – {fmtGiorno(f.periodo_al)}</span>
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 700 }}>{eur(f.importo_totale)}</td>
                      <td style={s.td}>
                        {f.numero_fattura ? `n. ${f.numero_fattura}` : '—'}
                        {f.scadenza && <span style={{ ...s.sotto, ...(f.scaduta ? { color: '#B91C1C', fontWeight: 700 } : {}) }}>scade il {fmtGiorno(f.scadenza)}</span>}
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.badge, backgroundColor: f.scaduta ? '#FEE2E2' : st.bg, color: f.scaduta ? '#B91C1C' : st.color }}>
                          {f.scaduta ? 'Scaduta' : st.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const s = {
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '0.75rem' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '1rem 1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid transparent' },
  cardRossa: { borderColor: '#FCA5A5', backgroundColor: '#FEF2F2' },
  cardLabel: { fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600 },
  cardValore: { fontSize: '1.35rem', fontWeight: 800, marginTop: '0.25rem', color: '#111827' },
  nota: { fontSize: '0.78rem', color: '#6B7280', margin: '0.6rem 0 1rem' },
  filtri: { display: 'inline-flex', backgroundColor: '#E5E7EB', borderRadius: '10px', padding: '3px', marginBottom: '1rem', flexWrap: 'wrap' },
  filtroBtn: { border: 'none', background: 'none', padding: '0.45rem 0.9rem', borderRadius: '8px', fontSize: '0.86rem', fontWeight: 600, color: '#4B5563', cursor: 'pointer', minHeight: '36px' },
  filtroAttivo: { backgroundColor: 'white', color: '#7C3AED', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' },
  box: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' },
  tabella: { width: '100%', borderCollapse: 'collapse', minWidth: '720px' },
  th: { padding: '0.65rem 0.8rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em', backgroundColor: '#F9FAFB' },
  tr: { borderTop: '1px solid #F3F4F6', cursor: 'pointer' },
  td: { padding: '0.7rem 0.8rem', fontSize: '0.87rem', verticalAlign: 'top' },
  sotto: { display: 'block', fontSize: '0.76rem', color: '#6B7280', marginTop: '0.1rem' },
  codice: { fontSize: '0.8rem', backgroundColor: '#F3F4F6', padding: '0.1rem 0.35rem', borderRadius: '4px' },
  prova: { marginLeft: '0.35rem', fontSize: '0.7rem', color: '#92400E', backgroundColor: '#FEF3C7', padding: '0.05rem 0.35rem', borderRadius: '4px' },
  badge: { display: 'inline-block', padding: '0.2rem 0.55rem', borderRadius: '6px', fontSize: '0.76rem', fontWeight: 700 },
  vuoto: { padding: '2rem', textAlign: 'center', color: '#6B7280' },
};
